import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, PenLine, Save, Loader2, ListChecks, Video } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export function MeetingProtocol({ m, meetingId, setM, load }) {
  const [minutes, setMinutes] = useState(m.minutes || "");
  const [savingMin, setSavingMin] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [editSummary, setEditSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState("");
  const [savingSummary, setSavingSummary] = useState(false);

  useEffect(() => { setMinutes(m.minutes || ""); }, [m.minutes]);

  const saveMinutes = async () => {
    setSavingMin(true);
    try {
      await api.put(`/meetings/${meetingId}`, { minutes });
      toast.success("Protokoll lagret");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingMin(false);
    }
  };

  const genSummary = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/meetings/${meetingId}/ai-summary`);
      setM((prev) => ({ ...prev, ai_summary: data.ai_summary }));
      toast.success("AI-sammendrag generert");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAiLoading(false);
    }
  };

  const startEditSummary = () => { setSummaryDraft(m.ai_summary || ""); setEditSummary(true); };

  const saveSummary = async () => {
    setSavingSummary(true);
    try {
      await api.put(`/meetings/${meetingId}`, { ai_summary: summaryDraft });
      setM((prev) => ({ ...prev, ai_summary: summaryDraft }));
      setEditSummary(false);
      toast.success("AI-sammendrag lagret");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingSummary(false);
    }
  };

  return (
    <>
      <div className="rounded-xl border border-border/60 bg-card p-6 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">Protokoll</h3>
        </div>
        <Textarea
          data-testid="minutes-textarea"
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
          rows={8}
          placeholder="Skriv protokoll / referat fra møtet …"
          className="resize-none"
        />
        <div className="mt-3 flex justify-end">
          <Button size="sm" onClick={saveMinutes} disabled={savingMin} className="gap-2" data-testid="save-minutes-btn">
            <Save className="h-4 w-4" /> {savingMin ? "Lagrer …" : "Lagre protokoll"}
          </Button>
        </div>
      </div>

      {m.video_log?.length ? (
        <div className="rounded-xl border border-border/60 bg-card p-6 shadow-sm" data-testid="video-log">
          <div className="mb-3 flex items-center gap-2">
            <Video className="h-4 w-4 text-muted-foreground" />
            <h3 className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">Møtelogg</h3>
          </div>
          <ul className="space-y-2">
            {m.video_log.map((e, i) => (
              <li key={i} className="flex items-center gap-2.5 text-sm" data-testid={`video-log-entry-${i}`}>
                <span className={`h-2 w-2 shrink-0 rounded-full ${e.event === "started" ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
                <span className="font-medium">{e.event === "started" ? "Videomøte startet" : "Videomøte avsluttet"}</span>
                {e.by ? <span className="truncate text-muted-foreground">· {e.by}</span> : null}
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">{formatDateTime(e.at)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-xl border border-primary/25 bg-primary/[0.04] p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary"><Sparkles className="h-5 w-5" strokeWidth={1.7} /></div>
            <div>
              <h3 className="font-headings text-base font-bold tracking-tight">AI-sammendrag</h3>
              <p className="text-xs text-muted-foreground">Automatisk oppsummering – kan redigeres og lagres</p>
            </div>
          </div>
          {!editSummary ? (
            <div className="flex items-center gap-2">
              {m.ai_summary ? (
                <Button size="sm" variant="ghost" onClick={startEditSummary} className="gap-2" data-testid="edit-summary-btn">
                  <PenLine className="h-4 w-4" /> Rediger
                </Button>
              ) : null}
              <Button size="sm" variant="outline" onClick={genSummary} disabled={aiLoading} className="gap-2" data-testid="ai-summary-btn">
                {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {aiLoading ? "Genererer …" : m.ai_summary ? "Regenerer med AI" : "Generer"}
              </Button>
            </div>
          ) : null}
        </div>
        {editSummary ? (
          <div className="mt-4">
            <Textarea data-testid="edit-summary-textarea" value={summaryDraft} onChange={(e) => setSummaryDraft(e.target.value)} rows={10} className="resize-none bg-background" />
            <div className="mt-3 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setEditSummary(false)} data-testid="cancel-summary-btn">Avbryt</Button>
              <Button size="sm" onClick={saveSummary} disabled={savingSummary} className="gap-2" data-testid="save-summary-btn">
                <Save className="h-4 w-4" /> {savingSummary ? "Lagrer …" : "Lagre"}
              </Button>
            </div>
          </div>
        ) : m.ai_summary ? (
          <div data-testid="ai-summary-content" className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
            {m.ai_summary}
          </div>
        ) : (
          <p className="mt-4 text-sm text-muted-foreground">
            Trykk «Generer» for å lage et profesjonelt sammendrag med vedtak og oppfølgingspunkter.
          </p>
        )}
      </motion.div>
    </>
  );
}
